import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { leafName } from "@/dnd";
import type { DraggableData } from "@/dnd/ids";
import {
  buildCopyPayloadFromRecord,
  type ExplorerCopySourceScope,
} from "@/dnd/payloads";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import { FileRecordTypeIcon } from "@cohesive/ui-components";
import { useDraggable } from "@dnd-kit/core";
import { Globe, Loader2, Trash2 } from "lucide-react";
import { ExplorerRow } from "./ExplorerRow";

/**
 * Pure file_record row. Wires a dnd-kit draggable carrying the explorer
 * copy payload (single record, or the full multi-select snapshot when
 * the row is part of a selection). The unified dispatcher owns
 * classification + dispatch — this row stays presentational.
 */
export function ExplorerFileRow({
  record,
  scope,
  selected,
  selectedRecords,
  onClick,
  onDoubleClick,
  onContextMenu,
  trailing,
  onDelete,
  dimmed,
  className,
  leadingTitle,
  sourcePlaced,
  sourceParentSectionId,
  dragNamespace,
  htmlProps: htmlPropsCaller,
}: {
  record: FileRecord;
  /** Scope context the row was rendered from — needed because wire
   *  `FileRecord` doesn't carry workspace_id / canvas_id directly. */
  scope: ExplorerCopySourceScope;
  selected?: boolean;
  /** When the row is part of a multi-select, drag the whole selection.
   *  `placed` is per-row and forwarded into each payload's
   *  `sourcePlaced` flag (canvas-source rows only) so the dispatcher
   *  can route stored vs. placed siblings correctly. */
  selectedRecords?: {
    record: FileRecord;
    scope: ExplorerCopySourceScope;
    placed?: boolean;
    /** Section the placed node currently lives in, or null for canvas
     *  root. Threaded into the payload so the classifier can detect
     *  "already in this section" / "already at root" reparent no-ops. */
    parentSectionId?: string | null;
  }[];
  onClick?: (e: React.MouseEvent) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  trailing?: React.ReactNode;
  /**
   * When provided, renders a hover-shown trash button in the trailing
   * slot. Click pops a quick confirm and then dispatches the delete
   * (we keep the confirm inline rather than a dialog so the
   * single-click-to-clean-up flow stays at one keystroke per record).
   */
  onDelete?: (record: FileRecord) => void;
  /** Visual-only dim treatment. Used by canvas scope to mark files
   *  that exist as records but have no node on the board. */
  dimmed?: boolean;
  /** Forwarded to the underlying ExplorerRow className. */
  className?: string;
  /** When set, the entire row is wrapped in a Radix tooltip (500ms
   *  hover delay) showing this text. Used by canvas scope for the
   *  "Not placed on canvas" hint on dimmed rows. */
  leadingTitle?: string;
  /** Forwarded into the drag payload's `sourcePlaced` flag. Used by the
   *  drop dispatcher to allow re-dropping a stored canvas file onto its
   *  own canvas to place a node (rather than a no-op). */
  sourcePlaced?: boolean;
  /** Forwarded into the drag payload's `sourceParentSectionId`. Lets
   *  the classifier short-circuit "already in this section / at root"
   *  reparent no-ops without consulting live canvas state. */
  sourceParentSectionId?: string | null;
  /** Caller-supplied namespace folded into the dnd-kit draggable id.
   *  The same FileRecord can be rendered in multiple lists at once
   *  (e.g. flat Files-on-canvas list + section-grouped Nodes view), and
   *  dnd-kit requires globally-unique draggable ids — without a
   *  namespace, two rows with the same `record.id` register twice and
   *  unmounting one wipes the other from dnd-kit's tracking, leaving
   *  the survivor un-draggable until the page reloads. Pass a stable
   *  per-call-site key (e.g. `"files"`, `"section:<sectionId>"`). */
  dragNamespace?: string;
  /** Extra HTML attributes merged into the underlying row element — e.g.
   *  onMouseEnter/onMouseLeave for canvas highlight events. Merged after
   *  dnd-kit listeners so callers can override or extend. */
  htmlProps?: React.HTMLAttributes<HTMLDivElement>;
}) {
  const draggableRows =
    selected && selectedRecords && selectedRecords.length > 1
      ? selectedRecords
      : [
          {
            record,
            scope,
            placed: sourcePlaced,
            parentSectionId: sourceParentSectionId,
          },
        ];
  const payloads = draggableRows.map(
    ({ record: r, scope: s, placed, parentSectionId }) => {
      // Prefer per-row state when the multi-select snapshot supplies
      // it; fall back to the primary row's props for the single-row
      // case (and for legacy callers that don't set per-row info).
      const opts: { placed?: boolean; parentSectionId?: string | null } = {};
      const effectivePlaced =
        placed !== undefined
          ? placed
          : r.id === record.id
            ? sourcePlaced
            : undefined;
      const effectiveParent =
        parentSectionId !== undefined
          ? parentSectionId
          : r.id === record.id
            ? sourceParentSectionId
            : undefined;
      if (effectivePlaced !== undefined) opts.placed = effectivePlaced;
      if (effectiveParent !== undefined) opts.parentSectionId = effectiveParent;
      return buildCopyPayloadFromRecord(
        r,
        s,
        Object.keys(opts).length > 0 ? opts : undefined,
      );
    },
  );
  const draggableData: DraggableData = {
    kind: "files",
    payloads,
    primaryName: leafName(record.path),
  };

  const draggable = useDraggable({
    id: dragNamespace
      ? `file-row:${dragNamespace}:${record.id}`
      : `file-row:${record.id}`,
    data: draggableData,
  });

  // While the upload is in flight, swap the leading file-type icon for
  // a spinner. Keeps the row a single tight line (no subtitle taking up
  // a second baseline) and gives a clearer "this row is working" signal
  // than an inline text label.
  const status = String(record.status);
  const isInFlight = status === "pending" || status === "processing";

  // Hover-shown trash button. Composed alongside any caller-supplied
  // `trailing` so a row can have both (e.g. status badge + delete).
  // `pointer-events-none` on the parent's group hover state would hide
  // the button from clicks, so we explicitly opt in via
  // `pointer-events-auto`.
  const trailingContent =
    onDelete || trailing ? (
      <span className="inline-flex items-center gap-1">
        {trailing}
        {onDelete && (
          <button
            type="button"
            aria-label={`Delete ${leafName(record.path)}`}
            title="Delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(record);
            }}
            // Don't let the trash button initiate a drag — `pointerdown`
            // bubbling into dnd-kit's draggable listeners would arm a
            // drag whenever the user clicked the icon.
            onPointerDown={(e) => e.stopPropagation()}
            className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/[0.08] hover:text-destructive pointer-events-auto"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    ) : undefined;

  // Title gets a small inline globe to the right of the filename when
  // the record is published, so users can scan the explorer for live
  // public artifacts without having to open each file's pane. The
  // filename keeps its own `min-w-0 truncate` so long names still
  // ellipsize before the badge — the badge stays visible at the right
  // edge of the available width.
  const fileName = leafName(record.path);
  const titleNode = record.published ? (
    <span className="flex items-center gap-1.5 min-w-0">
      <span className="truncate">{fileName}</span>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <Globe
            aria-label="Published"
            className="h-3 w-3 shrink-0 text-muted-foreground/70"
          />
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={4}>
          Published
        </TooltipContent>
      </Tooltip>
    </span>
  ) : (
    fileName
  );

  const row = (
    <ExplorerRow
      ref={draggable.setNodeRef}
      title={titleNode}
      leading={
        isInFlight ? (
          <span
            aria-label="Processing"
            className="flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center text-muted-foreground"
          >
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          </span>
        ) : (
          <span className="flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center">
            <FileRecordTypeIcon
              contentType={record.content_type}
              originType={record.origin_type}
              className="h-3.5 w-3.5"
            />
          </span>
        )
      }
      selected={selected}
      selectedChrome={false}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      trailing={trailingContent}
      className={cn(dimmed && "opacity-60", className)}
      htmlProps={{
        ...draggable.attributes,
        ...draggable.listeners,
        onMouseEnter: htmlPropsCaller?.onMouseEnter,
        onMouseLeave: htmlPropsCaller?.onMouseLeave,
        // Source row dimming while drag is active — the overlay pill
        // is the sole moving element so cards/rows feel identical.
        style: draggable.isDragging
          ? { opacity: 0.4, cursor: "grabbing" }
          : htmlPropsCaller?.style,
      }}
    />
  );

  // Outer host is the list-item for contiguous outline CSS; ExplorerRow
  // keeps the fill but not the outline host attrs (`selectedChrome={false}`).
  const selectedHostProps = {
    "data-rail-list-item": "",
    "data-rail-chrome": "",
    ...(selected ? { "data-rail-selected": "" } : {}),
  };

  // When the caller supplies `leadingTitle` (canvas-scope uses this
  // for the "Not placed on canvas" hint), wrap the whole row in a Radix
  // tooltip with a 500ms hover delay. Anywhere on the row triggers it,
  // so the affordance doesn't depend on the user precisely hitting the
  // file icon. No tooltip while the upload is in-flight — the spinner
  // already explains itself.
  //
  // The trigger needs a real DOM element (Radix `asChild` forwards its
  // pointer-event handlers onto its immediate child), and `ExplorerRow`
  // only forwards a fixed prop bag — so we wrap in a thin `<div>` that
  // becomes the hover target. It sits in the same flex column as the
  // other rows, so layout is unchanged.
  if (leadingTitle && !isInFlight) {
    return (
      <Tooltip delayDuration={500}>
        <TooltipTrigger asChild>
          <div {...selectedHostProps}>{row}</div>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={4}>
          {leadingTitle}
        </TooltipContent>
      </Tooltip>
    );
  }

  return <div {...selectedHostProps}>{row}</div>;
}
